import React, { useState } from "react";
import {
    MatrixWrapper, MatrixTable, MatrixThead, MatrixTbody,
    IntervLabel, DoseCell, CompletionCell, MiniBar, MiniBarFill,
    ViewToggle, ViewToggleBtn,
    ConditionalToggle, ToggleSwitch, 
} from "./ANCInterventionStyles";

/**
 * ANCInterventionMatrix — Visual matrix tracker for preventive treatments
 *
 * Shows rows = interventions, columns = dose slots
 * Green ✓ = given, Orange pulsing = due now, Grey ○ = not yet due
 */
export const ANCInterventionMatrix = (props) => {
    const { displayMatrix, showConditional, setShowConditional, openRecordDialog } = props;

    /* ─── Max dose columns (for table header) ─── */
    let maxDoses = 1;
    displayMatrix.forEach((item) => {
        if (item.totalDoses > maxDoses) maxDoses = item.totalDoses;
    });

    /* ─── Due now count ─── */
    let totalDue = 0;
    displayMatrix.forEach((item) => { totalDue += item.dueCount; });

    /* ─── Render dose cell ─── */
    const renderDoseCell = (item, dose) => {
        const clickable = dose.isDue && !dose.given;
        const handleClick = () => {
            if (clickable) openRecordDialog(item.key, dose.doseNumber);
        };
        return (
            <DoseCell
                key={dose.doseNumber}
                given={dose.given}
                isDue={dose.isDue}
                clickable={clickable}
                onClick={handleClick}
                title={dose.given
                    ? `${dose.label} — Given ${dose.dateGiven || ""}`
                    : dose.isDue
                        ? `${dose.label} — Click to record`
                        : `${dose.label} — Not yet due`}
            >
                {dose.given ? "✓" : dose.isDue ? "!" : "○"}
            </DoseCell>
        );
    };

    /* ═══ Matrix (table) View ═══ */
    const renderMatrix = () => (
        <MatrixWrapper>
            <MatrixTable>
                <MatrixThead>
                    <tr>
                        <th>Intervention</th>
                        {Array.from({ length: maxDoses }, (_, i) => (
                            <th key={i}>{`Dose ${i + 1}`}</th>
                        ))}
                        <th>Progress</th>
                    </tr>
                </MatrixThead>
                <MatrixTbody>
                    {displayMatrix.map((item) => (
                        <tr key={item.key}>
                            <td>
                                <IntervLabel>
                                    <div>
                                        <div className="iv-name">{item.label}</div>
                                        <div className="iv-sub">
                                            {item.conditional ? "Conditional" : item.category}
                                        </div>
                                    </div>
                                </IntervLabel>
                            </td>
                            {Array.from({ length: maxDoses }, (_, i) => {
                                const dose = item.doses[i];
                                if (!dose) return <td key={i}>—</td>;
                                return <td key={i}>{renderDoseCell(item, dose)}</td>;
                            })}
                            <td>
                                <CompletionCell>
                                    <MiniBar>
                                        <MiniBarFill percent={item.completionPercent} />
                                    </MiniBar>
                                    <span style={{
                                        fontSize: "0.72rem",
                                        fontWeight: 600,
                                        color: item.isComplete ? "#43a047" : "#757575",
                                    }}>
                                        {`${item.givenCount}/${item.totalDoses}`}
                                    </span>
                                </CompletionCell>
                            </td>
                        </tr>
                    ))}
                </MatrixTbody>
            </MatrixTable>
        </MatrixWrapper>
    );

    return (
        <div>
            {/* ─── Toggle + View Switch ─── */}
            <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                flexWrap: "wrap", gap: 12, marginBottom: 4,
            }}>
                <ConditionalToggle>
                    <ToggleSwitch
                        active={showConditional}
                        onClick={() => setShowConditional(!showConditional)}
                    />
                    <span>Show conditional treatments (HIV+, high-risk)</span>
                </ConditionalToggle>
            </div>

            {/* ─── Main Content ─── */}
            {renderMatrix()}

            {/* ─── Legend ─── */}
            <div style={{
                display: "flex", gap: 18, flexWrap: "wrap", marginTop: 8,
                fontSize: "0.76rem", color: "#78909c", paddingBottom: 16,
            }}>
                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <DoseCell given={true} style={{ width: 20, height: 20, fontSize: "0.65rem" }}>✓</DoseCell>
                    Given
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <DoseCell isDue={true} style={{ width: 20, height: 20, fontSize: "0.65rem" }}>!</DoseCell>
                    Due Now
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <DoseCell style={{ width: 20, height: 20, fontSize: "0.65rem" }}>○</DoseCell>
                    Not Yet Due
                </span>
            </div>
        </div>
    );
};
